/*
 * Shared DSA problem definition + prompt builder.
 * Used by the inline DsaCard (editor/tests) and by NanoChat (so the main
 * composer can send questions with the problem + current code as context).
 * Swap PROBLEM for a real problem-bank fetch later — the UI stays the same.
 */
export const PROBLEM = {
  num: 3,
  title: 'Longest Substring Without Repeating Characters',
  difficulty: 'Medium',
  tags: ['Hash Table', 'String', 'Sliding Window'],
  statement: 'Given a string s, find the length of the longest substring without repeating characters.',
  examples: [
    { input: 's = "abcabcbb"', output: '3', explanation: 'The answer is "abc", with the length of 3.' },
    { input: 's = "bbbbb"', output: '1', explanation: 'The answer is "b", with the length of 1.' },
  ],
  constraints: ['0 <= s.length <= 5 * 10⁴', 's consists of English letters, digits, symbols and spaces.'],
  testcases: [
    { name: 'Case 1', input: 's = "abcabcbb"', expected: '3' },
    { name: 'Case 2', input: 's = "bbbbb"', expected: '1' },
    { name: 'Case 3', input: 's = "pwwkew"', expected: '3' },
  ],
};

/* Per-language starter code for the current PROBLEM. Keyed by the LANGS values
   so the editor can seed the right template when you switch languages. */
export const STARTERS = {
  Python3: `class Solution:
    def lengthOfLongestSubstring(self, s: str) -> int:
        char_set = set()
        left = max_len = 0
        for right, ch in enumerate(s):
            while ch in char_set:
                char_set.remove(s[left])
                left += 1
            char_set.add(ch)
            max_len = max(max_len, right - left + 1)
        return max_len`,

  JavaScript: `/**
 * @param {string} s
 * @return {number}
 */
var lengthOfLongestSubstring = function (s) {
  const seen = new Set();
  let left = 0, maxLen = 0;
  for (let right = 0; right < s.length; right++) {
    while (seen.has(s[right])) {
      seen.delete(s[left]);
      left++;
    }
    seen.add(s[right]);
    maxLen = Math.max(maxLen, right - left + 1);
  }
  return maxLen;
};`,

  Java: `class Solution {
    public int lengthOfLongestSubstring(String s) {
        Set<Character> seen = new HashSet<>();
        int left = 0, maxLen = 0;
        for (int right = 0; right < s.length(); right++) {
            while (seen.contains(s.charAt(right))) {
                seen.remove(s.charAt(left));
                left++;
            }
            seen.add(s.charAt(right));
            maxLen = Math.max(maxLen, right - left + 1);
        }
        return maxLen;
    }
}`,

  'C++': `class Solution {
public:
    int lengthOfLongestSubstring(string s) {
        unordered_set<char> seen;
        int left = 0, maxLen = 0;
        for (int right = 0; right < s.size(); right++) {
            while (seen.count(s[right])) {
                seen.erase(s[left]);
                left++;
            }
            seen.insert(s[right]);
            maxLen = max(maxLen, right - left + 1);
        }
        return maxLen;
    }
};`,
};

export const LANGS = ['Python3', 'JavaScript', 'Java', 'C++'];

/* Back-compat: the inline DsaCard still imports STARTER (Python default). */
export const STARTER = STARTERS.Python3;

export const ASSIST_ACTIONS = [
  { key: 'hint', label: 'Hint', prompt: 'Give me a small hint to move forward, without revealing the full solution.' },
  { key: 'walk', label: 'Walkthrough', prompt: 'Walk me through the optimal approach step by step.' },
  { key: 'opt', label: 'Optimize', prompt: 'Analyze the time and space complexity of my code and suggest optimizations.' },
  { key: 'debug', label: 'Debug', prompt: 'Review my code for bugs or edge cases it might fail on, and explain them.' },
  { key: 'explain', label: 'Explain Code', prompt: 'Explain what my current code does, line by line.' },
];

/* Wrap a user instruction with the problem + current code as context.
   `problem` may be the loaded backend question (normalized to { num, title,
   difficulty, statement }); falls back to the static PROBLEM. */
export function buildDsaPrompt(userPrompt, code, lang, problem) {
  const p = problem || PROBLEM;
  const num = p.num ?? p.question_id ?? '';
  const statement = p.statement || p.problem_description || '';
  return `You are DataVerse.ai, an expert DSA coding assistant. The learner is solving:
Title: "${num}. ${p.title}" (${p.difficulty})
Statement: ${statement}

Their current ${lang} code:
\`\`\`
${code}
\`\`\`

${userPrompt}
Answer concisely using markdown; use fenced code blocks for any code.`;
}
